import axiosRestaurantInstance from './AxiosRestaurantInstance';
import axiosGroceryInstance from './AxiosGroceryInstance';
import { GROCERY_API_PATHS, RESTAURANT_API_PATHS } from '../constants/apiPaths';

const DATE_FORMAT_OPTIONS = ['YYYYMMDD', 'DDMMYYYY', 'DDMon'];

export function emptyOrderNumberForm() {
  return {
    orderNoEnabled: true,
    orderNoPrefix: '',
    orderNoIncludeDate: true,
    orderNoDateFormat: 'YYYYMMDD',
    orderNoPadWidth: '4',
    orderNoSeparator: '-',
    orderNoTimezone: 'Asia/Kolkata',
  };
}

function pick(raw, ...keys) {
  if (!raw || typeof raw !== 'object') return undefined;
  for (const key of keys) {
    if (raw[key] !== undefined && raw[key] !== null) return raw[key];
  }
  return undefined;
}

function asBool(value, fallback = true) {
  if (value === undefined || value === null || value === '') return fallback;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  const s = String(value).trim().toLowerCase();
  if (['1', 'true', 'yes', 'y', 'on'].includes(s)) return true;
  if (['0', 'false', 'no', 'n', 'off'].includes(s)) return false;
  return fallback;
}

export function orderNumberConfigToForm(raw) {
  if (!raw) return emptyOrderNumberForm();
  const dateFormat = String(
    pick(raw, 'orderNoDateFormat', 'order_no_date_format') ?? 'YYYYMMDD'
  ).trim();
  return {
    orderNoEnabled: asBool(pick(raw, 'orderNoEnabled', 'order_no_enabled'), true),
    orderNoPrefix: String(pick(raw, 'orderNoPrefix', 'order_no_prefix') ?? '').trim(),
    orderNoIncludeDate: asBool(pick(raw, 'orderNoIncludeDate', 'order_no_include_date'), true),
    orderNoDateFormat: DATE_FORMAT_OPTIONS.includes(dateFormat) ? dateFormat : 'YYYYMMDD',
    orderNoPadWidth: String(pick(raw, 'orderNoPadWidth', 'order_no_pad_width') ?? 4),
    orderNoSeparator: String(pick(raw, 'orderNoSeparator', 'order_no_separator') ?? '-'),
    orderNoTimezone: String(
      pick(raw, 'orderNoTimezone', 'order_no_timezone') ?? 'Asia/Kolkata'
    ).trim() || 'Asia/Kolkata',
  };
}

export function buildOrderNumberPayload(form, { updatedById } = {}) {
  const pad = Number.parseInt(String(form.orderNoPadWidth ?? '4'), 10);
  const payload = {
    orderNoEnabled: Boolean(form.orderNoEnabled),
    orderNoPrefix: String(form.orderNoPrefix ?? '').trim(),
    orderNoIncludeDate: Boolean(form.orderNoIncludeDate),
    orderNoDateFormat: DATE_FORMAT_OPTIONS.includes(form.orderNoDateFormat)
      ? form.orderNoDateFormat
      : 'YYYYMMDD',
    orderNoPadWidth: Number.isFinite(pad) ? Math.max(0, Math.min(pad, 10)) : 4,
    orderNoSeparator: String(form.orderNoSeparator ?? '-').slice(0, 5),
    orderNoTimezone: String(form.orderNoTimezone ?? 'Asia/Kolkata').trim() || 'Asia/Kolkata',
  };
  if (updatedById != null && updatedById !== '') {
    payload.updatedById = Number(updatedById);
  }
  return payload;
}

export function validateOrderNumberForm(form) {
  const pad = Number.parseInt(String(form.orderNoPadWidth ?? ''), 10);
  if (!Number.isFinite(pad) || pad < 0 || pad > 10) {
    return 'Pad width must be a number between 0 and 10.';
  }
  if (!DATE_FORMAT_OPTIONS.includes(form.orderNoDateFormat)) {
    return 'Choose a valid date format.';
  }
  if (!String(form.orderNoTimezone || '').trim()) {
    return 'Timezone is required.';
  }
  return null;
}

function authHeaders() {
  const token = localStorage.getItem('Token');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

export async function fetchOrderNumberConfig({ restaurantId, storeId } = {}) {
  if (storeId != null && String(storeId).trim() !== '') {
    const response = await axiosGroceryInstance.get(
      GROCERY_API_PATHS.storeOrderNumberConfig(storeId),
      { headers: authHeaders() }
    );
    return orderNumberConfigToForm(response.data);
  }
  const response = await axiosRestaurantInstance.get(
    RESTAURANT_API_PATHS.restaurantOrderNumberConfig(restaurantId),
    { headers: authHeaders() }
  );
  return orderNumberConfigToForm(response.data);
}

export async function saveOrderNumberConfig({ restaurantId, storeId, form, updatedById } = {}) {
  const payload = buildOrderNumberPayload(form, { updatedById });
  if (storeId != null && String(storeId).trim() !== '') {
    const response = await axiosGroceryInstance.put(
      GROCERY_API_PATHS.storeOrderNumberConfig(storeId),
      payload,
      { headers: authHeaders() }
    );
    return orderNumberConfigToForm(response.data);
  }
  const response = await axiosRestaurantInstance.put(
    RESTAURANT_API_PATHS.restaurantOrderNumberConfig(restaurantId),
    payload,
    { headers: authHeaders() }
  );
  return orderNumberConfigToForm(response.data);
}

export { DATE_FORMAT_OPTIONS };
